import { toastNotifications } from '@uag/react-core';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { useSolveConnectorPlacement } from 'api/queries/forming/forming';
import { buildSolveConnectorPlacementRequest } from 'app/modules/wallforming/components/setting-container/connector-placement/utils/buildSolveConnectorPlacementRequest';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';

interface AutomatedConnectorPlacementProps {
    setLoadingState: Dispatch<SetStateAction<boolean>>;
}

export const useGetAutomatedConnectorPlacement = ({ setLoadingState }: AutomatedConnectorPlacementProps) => {
    const { t } = useTranslation();
    const solveConnectorPlacementMutation = useSolveConnectorPlacement();
    const isPendingRef = useRef<boolean>(false);

    const { selectedPoiIndex } = usePlacementStore(
        useShallow((state) => ({
            selectedPoiIndex: state.poiSelection.current.index
        }))
    );

    const [searchParams] = useSearchParams();
    const sessionguid = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);

    return useCallback(async () => {
        if (selectedPoiIndex === undefined || isPendingRef.current) {
            return;
        }

        isPendingRef.current = true;
        setLoadingState(true);

        try {
            const state = usePlacementStore.getState();
            const payload = buildSolveConnectorPlacementRequest({
                state,
                sessionId: sessionguid,
                systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                poiIndex: selectedPoiIndex
            });

            const response = await solveConnectorPlacementMutation.mutateAsync({
                data: payload
            });

            if (!response.accepted) {
                toastNotifications.error({
                    title: t('error'),
                    message: `${response.errorCode}: ${response.message}`
                });
                return;
            } else {
                toastNotifications.success({ title: t('success'), message: t('successConnectorPlacement') });
            }
        } catch {
            toastNotifications.error({ title: t('error'), message: t('errorAutoConnectorPlacement') });
        } finally {
            isPendingRef.current = false;
            setLoadingState(false);
        }
    }, [selectedPoiIndex, sessionguid, setLoadingState, solveConnectorPlacementMutation, t]);
};
